import { forTemplate } from 'app/common/lib/util/i18n';
import classNames from 'classnames';
import Dotdotdot from 'react-dotdotdot';
import React, { useState, useEffect } from 'react';
import RouterLink from 'app/common/components/singlespa/router-link';
import { MemberAvatarUnconnected } from 'app/src/components/MemberAvatar';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { TemplatesTileSkeleton } from './TemplatesTileSkeleton';
import { smallestPreviewBiggerThan } from 'app/common/lib/util/preview';
import { useTemplateBackgroundImageScaleQuery } from '@trello/graphql/generated';
import { templateUrlSlug } from './Helpers';
import { Analytics } from '@trello/atlassian-analytics';
import styles from './TemplatesTile.less';
import { UnsplashTracker } from '@trello/unsplash';

const format = forTemplate('templates_tile');

function useBackgroundStyle(
  boardId: string,
  backgroundColor?: string,
  backgroundUrl?: string,
) {
  const css: React.CSSProperties = {};
  const { data, error, loading } = useTemplateBackgroundImageScaleQuery({
    variables: {
      idBoard: boardId,
    },
  });

  if (backgroundUrl) {
    let scaledBackgroundUrl = backgroundUrl;

    if (loading) {
      return {
        loading,
        templateBackgroundStyle: css,
      };
    }

    if (!error) {
      const backgroundImageScaled =
        (!!data &&
          !!data.board &&
          !!data.board.prefs &&
          !!data.board.prefs.backgroundImageScaled &&
          data.board.prefs.backgroundImageScaled) ||
        undefined;

      const preview =
        !!data && smallestPreviewBiggerThan(backgroundImageScaled, 300, 150);
      scaledBackgroundUrl = (!!preview && preview.url) || backgroundUrl;
    }

    css.backgroundImage = `url('${scaledBackgroundUrl}')`;
    css.backgroundSize = 'cover';
    css.backgroundPosition = 'center';
  } else {
    css.backgroundColor = backgroundColor || styles.trelloBlue;
  }

  return {
    loading: false,
    templateBackgroundStyle: css,
  };
}

function useBackgroundAfterStyle(hover: boolean) {
  const css: React.CSSProperties = {};
  css.opacity = hover ? 1 : 0;
  return css;
}

interface TemplatesTileProps {
  avatarShape: string;
  avatarUrl: string;
  category: string;
  backgroundColor?: string;
  backgroundUrl?: string;
  blurb: string;
  boardId: string;
  byline: string;
  idOrganization: string;
  shortLink: string;
  title: string;
  isFeatured?: boolean;
}

export const TemplatesTile: React.FunctionComponent<TemplatesTileProps> = ({
  avatarShape,
  avatarUrl,
  backgroundColor,
  backgroundUrl,
  blurb,
  boardId,
  byline,
  category,
  idOrganization,
  shortLink,
  title,
  isFeatured,
}) => {
  useEffect(() => {
    if (backgroundUrl) {
      UnsplashTracker.trackOncePerInterval(backgroundUrl);
    }
  }, [backgroundUrl]);

  const [hover, setHover] = useState(false);
  const onHoverStart = () => {
    setHover(true);
  };
  const onHoverEnd = () => {
    setHover(false);
  };

  const onTileClick = () => {
    trackUe({
      category: Noun.TEMPLATES_GALLERY,
      verb: Verb.OPENS,
      directObj: Noun.TEMPLATES_STORY_PAGE,
      context: {
        boardId: boardId || '',
        teamId: idOrganization || '',
      },
    });

    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'tile',
      actionSubjectId: 'templateTile',
      source: 'templateGalleryScreen',
    });
  };

  const routerLinkProps = {
    className: styles.allText,
    href: `/templates/${templateUrlSlug(category, title, shortLink)}`,
    title: title,
    onClick: onTileClick,
  };

  const { loading, templateBackgroundStyle } = useBackgroundStyle(
    boardId,
    backgroundColor,
    backgroundUrl,
  );
  const templateBackgroundAfterStyle = useBackgroundAfterStyle(hover);

  return (
    <div
      className={classNames(styles.tileContainer, {
        [styles.isFeatured]: isFeatured,
      })}
    >
      <RouterLink {...routerLinkProps}>
        {loading ? (
          <TemplatesTileSkeleton />
        ) : (
          <div
            className={styles.templateBackground}
            style={templateBackgroundStyle}
          />
        )}
        <div
          className={styles.templateBackgroundAfter}
          style={templateBackgroundAfterStyle}
          onMouseEnter={onHoverStart}
          onMouseLeave={onHoverEnd}
        />
        <div className={styles.templateInfo}>
          <MemberAvatarUnconnected
            className={styles.memberAvatar}
            fullName={byline}
            avatars={{
              '30': `${avatarUrl}/30.png`,
              '50': `${avatarUrl}/50.png`,
              '170': `${avatarUrl}/170.png`,
            }}
            size={48}
            avatarClassName={classNames(styles.avatar, {
              [styles.orgAvatar]: avatarShape === 'square',
            })}
          />
          <Dotdotdot clamp={2} useNativeClamp>
            <div className={styles.title} title={title}>
              {title}
            </div>
          </Dotdotdot>
          <div className={styles.author}>
            {format('by', {
              author: byline,
            })
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&')}
          </div>
          <Dotdotdot clamp={3} useNativeClamp>
            <div className={styles.blurb} title={blurb}>
              {blurb}
            </div>
          </Dotdotdot>
        </div>
      </RouterLink>
    </div>
  );
};
